import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ExclamationTriangleIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { productService } from '../../services/productService';
import toast from 'react-hot-toast';

interface CorrectPreOrderModalProps {
    isOpen: boolean;
    onClose: () => void;
    invoiceId: string;
    onSuccess: () => void;
}

export const CorrectPreOrderModal: React.FC<CorrectPreOrderModalProps> = ({ isOpen, onClose, invoiceId, onSuccess }) => {
    const [loading, setLoading] = useState(true);
    const [executing, setExecuting] = useState(false);
    const [data, setData] = useState<{
        matchCount: number;
        safeToDeleteCount: number;
        warningCount: number;
    } | null>(null);

    useEffect(() => {
        if (isOpen && invoiceId) {
            checkPreOrders();
        }
    }, [isOpen, invoiceId]);

    const checkPreOrders = async () => {
        setLoading(true);
        try {
            const response = await productService.checkInvoicePreOrders(invoiceId);
            if (response.success) {
                setData(response.data);
            } else {
                toast.error(response.message || 'Error al analizar Pre-orders');
                onClose();
            }
        } catch (error: any) {
            console.error('Error checking preorders:', error);
            toast.error(error?.response?.data?.message || 'Error de conexión');
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleExecute = async () => {
        if (!data || data.matchCount === 0) return;

        setExecuting(true);
        const loadingToast = toast.loading('Ejecutando corrección inteligente...', { duration: 0 });
        try {
            const response = await productService.correctInvoicePreOrders(invoiceId);
            if (response.success) {
                toast.success(`Se corrigieron ${response.data.correctedCount} pre-orders y se eliminaron ${response.data.deletedCount} duplicados.`, { id: loadingToast, duration: 5000 });
                onSuccess();
            } else {
                toast.error(response.message || 'La corrección falló.', { id: loadingToast });
            }
        } catch (error: any) {
            console.error('Error in correction:', error);
            toast.error(error?.response?.data?.message || 'Falló la ejecución de corrección', { id: loadingToast });
        } finally {
            setExecuting(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => !executing && onClose()} />
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col overflow-hidden relative animate-bounce-in">

                {/* Header */}
                <div className="bg-amber-50 border-b border-amber-100 p-5 flex items-start gap-4">
                    <div className="bg-amber-100 p-2 rounded-full text-amber-600 flex-shrink-0 mt-1 shadow-sm">
                        <WrenchScrewdriverIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-amber-900 leading-tight">Corregir Pre-orders <br /> (Invoice {invoiceId})</h3>
                        <p className="text-sm text-amber-700 mt-1">
                            Esta herramienta re-sincroniza pre-orders huérfanos con los productos de este Invoice, eliminando duplicados insertados por error.
                        </p>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 bg-gray-50 border-b border-gray-100 min-h-[140px] flex justify-center items-center">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center w-full">
                            <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent flex items-center justify-center rounded-full animate-spin shadow-sm">
                            </div>
                            <span className="mt-3 text-sm text-gray-500 font-medium">Analizando Invoice...</span>
                        </div>
                    ) : !data ? (
                        <div className="text-red-500 text-sm">No se pudo cargar la información.</div>
                    ) : data.matchCount === 0 ? (
                        <div className="flex flex-col items-center">
                            <ExclamationTriangleIcon className="w-12 h-12 text-slate-300 mb-2" />
                            <p className="text-slate-600 font-medium text-center">No se detectaron Pre-orders huérfanos <br /> compatibles con este Invoice.</p>
                        </div>
                    ) : (
                        <div className="w-full">
                            <ul className="space-y-4">
                                <li className="flex justify-between items-center text-sm bg-white p-3 rounded-lg shadow-sm border border-gray-200">
                                    <span className="text-gray-600 font-medium">Pre-orders compatibles encontrados:</span>
                                    <span className="font-bold text-indigo-700 text-lg">{data.matchCount}</span>
                                </li>
                                <li className="flex justify-between items-center text-sm bg-white p-3 rounded-lg shadow-sm border border-gray-200">
                                    <span className="text-gray-600 font-medium">Duplicados seguros de eliminar:</span>
                                    <span className="text-green-600 font-bold text-lg">{data.safeToDeleteCount}</span>
                                </li>
                                {data.warningCount > 0 && (
                                    <li className="flex flex-col text-sm bg-red-50 p-4 rounded-lg shadow-inner border border-red-200">
                                        <div className="flex justify-between items-center w-full">
                                            <span className="text-red-800 font-bold flex items-center gap-1"><ExclamationTriangleIcon className="w-4 h-4" /> Duplicados con dependencias (Ventas):</span>
                                            <span className="text-red-700 font-bold text-lg">{data.warningCount}</span>
                                        </div>
                                        <p className="text-red-600 text-xs mt-2">
                                            Estos duplicados no se eliminarán para proteger la integridad de ventas activas. Sólamente se conectará el Pre-order al Invoice.
                                        </p>
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Footer Buttons */}
                <div className="p-5 flex justify-end gap-3 bg-white">
                    <button
                        onClick={onClose}
                        disabled={executing}
                        className="px-5 py-2.5 text-gray-700 hover:bg-gray-100 font-medium rounded-xl transition-colors disabled:opacity-50"
                    >
                        {data?.matchCount === 0 ? 'Cerrar' : 'Cancelar'}
                    </button>
                    <button
                        onClick={handleExecute}
                        disabled={executing || !data || data.matchCount === 0 || loading}
                        className="px-6 py-2.5 bg-amber-600 text-white font-bold rounded-xl hover:bg-amber-700 active:scale-95 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:shadow-none flex items-center gap-2"
                    >
                        {executing ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Ejecutando...
                            </>
                        ) : (
                            'Ejecutar Corrección'
                        )}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};
